import { useState, useRef, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { Loader2, Plus, Minus, Trash2, Check, X, Search, ShoppingBag, Gift } from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { trpc } from '~/api/trpc';
import { AutoComplete } from '~/components/ui/autocomplete';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '~/components/ui/dialog';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '~/components/ui/select';
import { PlateEditor } from '~/components/editor';
import type { PlateEditorRef } from '~/components/editor/plate-editor';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import { ScrollArea } from '~/components/ui/scroll-area';
import { Separator } from '~/components/ui/separator';
import { Checkbox } from '~/components/ui/checkbox';
import { cn } from '~/lib/utils';
import type { MedicalRecordNoTreatmentReason } from '@server/prisma-generated/enums';
import { NO_TREATMENT_REASON_OPTIONS } from './noTreatmentReasons';
import LocalDateTime from '~/lib/localdatetime';
import { useUserStore } from '~/stores/user-store';

// ============================================
// Types
// ============================================

type Medication = {
    id: string;
    medicamentId: string;
    regionId: string;
    medicationQuantity: number;
    medicationUnitId: string;
    notes: string;
};

type AddTreatmentDialogProps = {
    customerId: number;
    treatmentId?: string | null;
    /** When true, skip loading incomplete treatment and always start a new treatment at step 1 */
    forceNew?: boolean;
    /** Force opening in attribution step (step 1) for editing attributions only */
    forceAttributionStep?: boolean;
    /** When starting a new treatment, preselect this appointment in step 1 */
    preselectedAppointmentId?: string | null;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSuccess?: () => void;
};

type CurrentStep = 'ATTRIBUTION_STEP' | 'MEDICAL_DOCUMENTATION_STEP';

type NewVariantSelection = {
    id: string;
    treatmentVariantId: string;
    name: string;
    treatmentName?: string;
    bundleId?: string | null;
    bundleName?: string;
    priceCents?: number;
    bundleTotalPrice?: number;
    availableBundles?: BundleOption[];
};

type BundleOption = {
    id: string;
    name: string;
    unitPriceGross: number;
    unitQuantity: number;
};

type CartItemBundleOverride = { bundleId: string | null; bundleName?: string; bundleTotalPrice?: number };

type ProductSelection = {
    physicalProductId: string;
    name: string;
    unitPriceGross?: number;
};

type IncompleteTreatmentData = {
    id: string;
    appointmentId: string | null;
    diagnosis: string;
    mainIcdId?: string | null;
    mainIcdDisplay?: string | null;
    secondaryIcdId?: string | null;
    secondaryIcdDisplay?: string | null;
    notes: string;
    cartItemAttributions: Array<{
        cartItem?: { id: string } | null;
        treatmentVariantId?: string | null;
        treatmentVariant?: { id: string; name: string; priceCents?: number; treatment?: { name: string } | null } | null;
        bundleId?: string | null;
        bundle?: { id: string; name: string; unitPriceGross: number; unitQuantity: number } | null;
        physicalProductId?: string | null;
        physicalProduct?: { id: string; name: string; unitPriceGross?: number } | null;
    }>;
    medications: Array<{
        id: string;
        medicamentId: string | null;
        regionId: string | null;
        medicament?: { id: string; name: string; unit: { id: string; name: string } | null } | null;
        region?: { id: string; name: string } | null;
        medicationQuantity: unknown;
        medicationUnitId?: string | null;
        notes: string;
    }>;
};

type AttributionStepProps = {
    customerId: number;
    treatmentId?: string | null;
    treatmentDateTime: LocalDateTime;
    selectedAppointmentId: string | null;
    setSelectedAppointmentId: (id: string | null) => void;
    initialAppointmentId?: string | null;
    initialCartItemIds?: Set<string>;
    initialNewVariants?: NewVariantSelection[];
    initialCartItemBundleOverrides?: Map<string, CartItemBundleOverride>;
    initialProducts?: ProductSelection[];
    initialNoTreatmentReason?: MedicalRecordNoTreatmentReason | null;
    isEditMode?: boolean;
    onSaveAndProceed: (treatmentId: string) => void;
    onCancel: () => void;
};

type MedicalDocumentationStepProps = {
    customerId: number;
    treatmentId: string;
    treatmentDateTime: LocalDateTime;
    initialData?: IncompleteTreatmentData;
    isEditMode?: boolean;
    isReEdit?: boolean;
    storeId?: number;
    onComplete: () => void;
    onBack: (() => void) | null;
    onCancel: () => void;
};

// ============================================
// Constants & Helpers
// ============================================

const createEmptyMedication = (): Medication => ({
    id: crypto.randomUUID(),
    medicamentId: '',
    regionId: '',
    medicationQuantity: 1,
    medicationUnitId: '',
    notes: '',
});

const DEFAULT_TIMEZONE = 'Europe/Berlin' as const;

const formatCentsAsEuro = (cents: number) => (cents / 100).toFixed(2).replace('.', ',') + ' €';
const formatDecimalAsEuro = (value: number) => Number(value).toFixed(2).replace('.', ',') + ' €';

const formatAppointmentOption = (appointment: {
    steps?: { dateFrom?: Date | string | null }[];
    originalTreatmentVariants?: { treatmentVariant?: { name?: string } | null }[];
}) => {
    const firstStep = appointment.steps?.[0];
    const dateStr = firstStep?.dateFrom
        ? format(new Date(firstStep.dateFrom), 'dd.MM.yyyy HH:mm', { locale: de })
        : 'Kein Datum';
    const treatments = appointment.originalTreatmentVariants
        ?.map((tv) => tv.treatmentVariant?.name)
        .filter(Boolean)
        .join(', ') || 'Keine Behandlung';
    return `${dateStr} - ${treatments}`;
};

// ============================================
// Practitioner Badge
// ============================================

const PractitionerBadge = () => {
    const user = useUserStore((s) => s.user);
    if (!user) return null;
    return (
        <div>
            <Badge className="bg-blue-100 text-blue-800 border-blue-300">
                {user.firstName} {user.lastName}
            </Badge>
        </div>
    );
};

// ============================================
// Bundle Toggle
// ============================================

const BundleToggle = ({
    bundles,
    selectedBundleId,
    onChange,
    disabled,
}: {
    bundles: BundleOption[];
    selectedBundleId?: string | null;
    onChange: (bundle: BundleOption | null) => void;
    disabled?: boolean;
}) => {
    const [isPickerOpen, setIsPickerOpen] = useState(false);
    const isChecked = !!selectedBundleId;

    const handleCheckedChange = (checked: boolean) => {
        if (!checked) {
            onChange(null);
        } else if (bundles.length === 1) {
            onChange(bundles[0]);
        } else {
            setIsPickerOpen(true);
        }
    };

    return (
        <>
            <label className="flex items-center gap-1.5 text-xs cursor-pointer shrink-0">
                <Checkbox
                    checked={isChecked}
                    onCheckedChange={(checked) => handleCheckedChange(checked === true)}
                    disabled={disabled}
                />
                Als Bundle
            </label>
            <Dialog open={isPickerOpen} onOpenChange={setIsPickerOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Bundle wählen</DialogTitle>
                        <DialogDescription>
                            Für diese Leistung sind mehrere Bundles verfügbar.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2 py-2">
                        {bundles.map((bundle) => (
                            <Button
                                key={bundle.id}
                                variant="outline"
                                className="w-full justify-between h-auto py-3"
                                onClick={() => {
                                    onChange(bundle);
                                    setIsPickerOpen(false);
                                }}
                            >
                                <div className="text-left">
                                    <div className="font-medium">{bundle.name}</div>
                                    <div className="text-xs text-muted-foreground">
                                        {bundle.unitQuantity}x Behandlung – {formatDecimalAsEuro(Number(bundle.unitPriceGross))}
                                    </div>
                                </div>
                                <span className="text-sm font-medium">
                                    {formatDecimalAsEuro(Number(bundle.unitPriceGross) * bundle.unitQuantity)}
                                </span>
                            </Button>
                        ))}
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};

// ============================================
// Step 1: Attribution Step
// ============================================

const ALLOWED_APPOINTMENT_STATUSES = ['SCHEDULED', 'COMPLETED', 'REQUESTED'];

const AttributionStep = ({
    customerId,
    treatmentId,
    treatmentDateTime,
    selectedAppointmentId,
    setSelectedAppointmentId,
    initialAppointmentId,
    initialCartItemIds,
    initialNewVariants,
    initialCartItemBundleOverrides,
    initialProducts,
    initialNoTreatmentReason,
    isEditMode,
    onSaveAndProceed,
    onCancel,
}: AttributionStepProps) => {
    const [selectedCartItemIds, setSelectedCartItemIds] = useState<Set<string>>(initialCartItemIds ?? new Set());
    const [newVariants, setNewVariants] = useState<NewVariantSelection[]>(initialNewVariants ?? []);
    const [cartItemBundleOverrides, setCartItemBundleOverrides] = useState<Map<string, CartItemBundleOverride>>(initialCartItemBundleOverrides ?? new Map());
    const [selectedProducts, setSelectedProducts] = useState<ProductSelection[]>(initialProducts ?? []);
    const [productSearchValue, setProductSearchValue] = useState('');
    const [productSelectedValue, setProductSelectedValue] = useState('');
    const [variantSearchValue, setVariantSearchValue] = useState('');
    const [variantSelectedValue, setVariantSelectedValue] = useState('');
    const [noTreatmentPerformed, setNoTreatmentPerformed] = useState(!!initialNoTreatmentReason);
    const [noTreatmentReason, setNoTreatmentReason] = useState<MedicalRecordNoTreatmentReason | null>(initialNoTreatmentReason ?? null);
    const [cartItemAvailableBundles, setCartItemAvailableBundles] = useState<Map<string, BundleOption[]>>(new Map());
    const bundleFetchRequestedRef = useRef<Set<string>>(new Set());

    const utils = trpc.useUtils();

    const { data: appointments } = trpc.appointments.getByCustomerId.useQuery(
        { customerId },
        { enabled: true }
    );

    const filteredAppointments = useMemo(
        () => (appointments ?? []).filter((a) => ALLOWED_APPOINTMENT_STATUSES.includes(a.status)),
        [appointments]
    );

    const { data: appointmentDetails, isLoading: isLoadingDetails } =
        trpc.medicalRecord.getAppointmentDetailsForTreatment.useQuery(
            { appointmentId: selectedAppointmentId! },
            { enabled: !!selectedAppointmentId }
        );

    const selectedAppointmentStoreId = useMemo(() => {
        if (!selectedAppointmentId || !filteredAppointments) return undefined;
        const apt = filteredAppointments.find(a => a.id === selectedAppointmentId);
        return apt?.store?.id ?? undefined;
    }, [selectedAppointmentId, filteredAppointments]);

    const { data: variantSearchResults, isLoading: isSearchingVariants } =
        trpc.medicalRecord.searchTreatmentVariants.useQuery(
            { search: variantSearchValue, storeId: selectedAppointmentStoreId },
            { enabled: variantSearchValue.length >= 1 }
        );

    const { data: productSearchResults, isLoading: isSearchingProducts } =
        trpc.physicalProducts.searchProducts.useQuery(
            { search: productSearchValue },
            { enabled: productSearchValue.length >= 1 }
        );

    const saveAttribution = trpc.medicalRecord.saveAttributionStep.useMutation({
        onSuccess: (data) => {
            utils.medicalRecord.getByCustomerId.invalidate({ customerId });
            utils.medicalRecord.getIncompleteTreatment.invalidate({ customerId });
            utils.qm.getMyOutstandingDocumentation.invalidate();
            toast.success('Zuordnung gespeichert');
            onSaveAndProceed(data.id);
        },
        onError: (error) => {
            toast.error(`Fehler beim Speichern: ${error.message}`);
        },
    });

    const updateAttribution = trpc.medicalRecord.updateAttributionStep.useMutation({
        onSuccess: () => {
            utils.medicalRecord.getByCustomerId.invalidate({ customerId });
            utils.medicalRecord.getIncompleteTreatment.invalidate({ customerId });
            utils.qm.getMyOutstandingDocumentation.invalidate();
            toast.success('Zuordnung aktualisiert');
            onSaveAndProceed(treatmentId!);
        },
        onError: (error) => {
            toast.error(`Fehler beim Speichern: ${error.message}`);
        },
    });

    const prevAppointmentIdRef = useRef<string | null>(null);

    // Sync persisted cart selections when loading for edit
    useEffect(() => {
        if (initialCartItemIds !== undefined) {
            setSelectedCartItemIds(initialCartItemIds);
        }
    }, [initialCartItemIds]);

    // Sync persisted variant-only selections when loading for edit
    useEffect(() => {
        if (initialNewVariants !== undefined) {
            setNewVariants(initialNewVariants);
        }
    }, [initialNewVariants]);

    // Sync persisted bundle overrides when loading for edit
    useEffect(() => {
        if (initialCartItemBundleOverrides !== undefined) {
            setCartItemBundleOverrides(initialCartItemBundleOverrides);
        }
    }, [initialCartItemBundleOverrides]);

    // Sync persisted products when loading for edit
    useEffect(() => {
        if (initialProducts !== undefined) {
            setSelectedProducts(initialProducts);
        }
    }, [initialProducts]);

    // Sync persisted no-treatment reason when loading for edit
    useEffect(() => {
        if (initialNoTreatmentReason !== undefined) {
            setNoTreatmentPerformed(!!initialNoTreatmentReason);
            setNoTreatmentReason(initialNoTreatmentReason);
        }
    }, [initialNoTreatmentReason]);

    const setNoTreatment = (checked: boolean) => {
        setNoTreatmentPerformed(checked);
        if (!checked) {
            setNoTreatmentReason(null);
        }
    };

    // Clear selections only when user changes to a different appointment (not on initial load in edit mode)
    useEffect(() => {
        const prevId = prevAppointmentIdRef.current;
        prevAppointmentIdRef.current = selectedAppointmentId;

        const appointmentChanged = prevId !== selectedAppointmentId;
        const isEditLoadWithSameAppointment =
            initialAppointmentId != null && selectedAppointmentId === initialAppointmentId && prevId === null;

        if (appointmentChanged && !isEditLoadWithSameAppointment && selectedAppointmentId != null) {
            setSelectedCartItemIds(new Set());
            setNewVariants([]);
        }
    }, [selectedAppointmentId, initialAppointmentId]);

    // Default-select the latest appointment that is today or in the past (new treatments only)
    const hasAutoSelectedRef = useRef(false);
    useEffect(() => {
        if (isEditMode || hasAutoSelectedRef.current) {
            return;
        }
        if (selectedAppointmentId != null || initialAppointmentId != null) {
            hasAutoSelectedRef.current = true;
            return;
        }
        if (filteredAppointments.length === 0) {
            return;
        }

        const now = Date.now();
        const candidates = filteredAppointments
            .map((a) => ({ id: a.id, dateFrom: a.steps?.[0]?.dateFrom ? new Date(a.steps[0].dateFrom).getTime() : null }))
            .filter((a): a is { id: string; dateFrom: number } => a.dateFrom != null && a.dateFrom <= now)
            .sort((a, b) => b.dateFrom - a.dateFrom);

        if (candidates.length > 0) {
            hasAutoSelectedRef.current = true;
            setSelectedAppointmentId(candidates[0].id);
        }
    }, [filteredAppointments, selectedAppointmentId, initialAppointmentId, isEditMode, setSelectedAppointmentId]);

    const variantOptions = useMemo(() => {
        if (!variantSearchResults) {
            return [];
        }
        return variantSearchResults.map((v) => ({
            value: v.id,
            label: v.name,
            renderItem: (
                <div className="flex items-center justify-between w-full gap-2">
                    <div className="flex flex-col min-w-0">
                        <span className="text-xs text-muted-foreground">{v.treatment.name}</span>
                        <span>{v.name}</span>
                    </div>
                    {v.priceCents > 0 && (
                        <span className="text-xs text-muted-foreground shrink-0">{formatCentsAsEuro(v.priceCents)}</span>
                    )}
                </div>
            ),
        }));
    }, [variantSearchResults]);

    const productOptions = useMemo(() => {
        if (!productSearchResults?.data) return [];
        const existingIds = new Set(selectedProducts.map((p) => p.physicalProductId));
        return productSearchResults.data
            .filter((p: any) => !existingIds.has(p.id))
            .map((p: any) => ({
                value: p.id,
                label: p.name,
                renderItem: (
                    <div className="flex items-center justify-between w-full gap-2">
                        <span className="min-w-0 truncate">{p.name}</span>
                        {Number(p.unitPriceGross) > 0 && (
                            <span className="text-xs text-muted-foreground shrink-0">{formatDecimalAsEuro(p.unitPriceGross)}</span>
                        )}
                    </div>
                ),
            }));
    }, [productSearchResults, selectedProducts]);

    const handleProductSelect = (productId: string) => {
        if (!productId) return;
        const product = productSearchResults?.data?.find((p: any) => p.id === productId);
        if (product) {
            setSelectedProducts((prev) =>
                prev.some((p) => p.physicalProductId === productId)
                    ? prev
                    : [...prev, { physicalProductId: productId, name: product.name, unitPriceGross: Number(product.unitPriceGross) }]
            );
        }
        setProductSearchValue('');
        setProductSelectedValue('');
    };

    const handleRemoveProduct = (productId: string) => {
        setSelectedProducts((prev) => prev.filter((p) => p.physicalProductId !== productId));
    };

    const fetchBundlesForVariant = async (treatmentVariantId: string, storeId: number): Promise<BundleOption[]> => {
        try {
            const bundles = await utils.medicalRecord.getBundlesForVariant.fetch({ treatmentVariantId, storeId });
            return bundles.map((b) => ({
                id: b.id,
                name: b.name,
                unitPriceGross: Number(b.unitPriceGross),
                unitQuantity: b.unitQuantity,
            }));
        } catch {
            return [];
        }
    };

    const handleVariantSelect = (variantId: string) => {
        if (!variantId || !selectedAppointmentId || !selectedAppointmentStoreId) {
            return;
        }
        const variant = variantSearchResults?.find((v) => v.id === variantId);
        if (!variant) return;

        setVariantSearchValue('');
        setVariantSelectedValue('');

        setNewVariants((prev) => [
            ...prev,
            { id: crypto.randomUUID(), treatmentVariantId: variantId, name: variant.name, treatmentName: variant.treatment.name, priceCents: variant.priceCents },
        ]);
    };

    const handleRemoveCartItem = (cartItemId: string) => {
        setSelectedCartItemIds((prev) => {
            const next = new Set(prev);
            next.delete(cartItemId);
            return next;
        });
    };

    const handleRemoveNewVariant = (id: string) => {
        setNewVariants((prev) => prev.filter((v) => v.id !== id));
    };

    const toggleCartItem = (id: string) => {
        if (selectedCartItemIds.has(id)) {
            setSelectedCartItemIds((prev) => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
            setCartItemBundleOverrides((prev) => {
                const next = new Map(prev);
                next.delete(id);
                return next;
            });
            return;
        }

        setSelectedCartItemIds((prev) => {
            const next = new Set(prev);
            next.add(id);
            return next;
        });
    };

    const handleSave = () => {
        if (!selectedAppointmentId) {
            toast.error('Bitte wählen Sie einen Termin aus');
            return;
        }

        const selectedCount = selectedCartItemIds.size + newVariants.length;
        if (!noTreatmentPerformed && selectedCount === 0) {
            toast.error('Bitte wählen Sie mindestens eine Leistung aus');
            return;
        }

        if (noTreatmentPerformed && !noTreatmentReason) {
            toast.error('Bitte wählen Sie einen Grund aus');
            return;
        }

        const noTreatmentFields = { noTreatmentReason: noTreatmentPerformed ? noTreatmentReason : null };

        const serviceAttributions = noTreatmentPerformed
            ? []
            : [
                ...Array.from(selectedCartItemIds).map((cartItemId) => ({
                    cartItemId,
                    bundleId: cartItemBundleOverrides.get(cartItemId)?.bundleId ?? undefined,
                    attributionRate: 1.00,
                })),
                ...newVariants.map((v) => ({
                    treatmentVariantId: v.treatmentVariantId,
                    bundleId: v.bundleId ?? undefined,
                    attributionRate: 1.00,
                })),
            ];

        const productAttributions = selectedProducts.map((p) => ({
            physicalProductId: p.physicalProductId,
            attributionRate: 1.00,
        }));

        const cartItemAttributions = [...serviceAttributions, ...productAttributions];

        if (treatmentId) {
            updateAttribution.mutate({
                customerId,
                treatmentId,
                treatmentDateLocal: treatmentDateTime.toJson(),
                appointmentId: selectedAppointmentId!,
                ...noTreatmentFields,
                cartItemAttributions,
            });
        } else {
            saveAttribution.mutate({
                customerId,
                treatmentDateLocal: treatmentDateTime.toJson(),
                appointmentId: selectedAppointmentId!,
                ...noTreatmentFields,
                cartItemAttributions,
            });
        }
    };

    const bundleFields = (bundle: BundleOption | null) => ({
        bundleId: bundle?.id ?? null,
        bundleName: bundle?.name,
        bundleTotalPrice: bundle ? Number(bundle.unitPriceGross) * bundle.unitQuantity : undefined,
    });

    const setNewVariantBundle = (rowId: string, bundle: BundleOption | null) => {
        setNewVariants((prev) => prev.map((v) => (v.id === rowId ? { ...v, ...bundleFields(bundle) } : v)));
    };

    const setCartItemBundle = (cartItemId: string, bundle: BundleOption | null) => {
        setCartItemBundleOverrides((prev) => {
            const next = new Map(prev);
            if (bundle) {
                next.set(cartItemId, bundleFields(bundle));
            } else {
                next.delete(cartItemId);
            }
            return next;
        });
    };

    const isMutating = saveAttribution.isPending || updateAttribution.isPending;
    const cartItems = appointmentDetails?.cartItems ?? [];

    // Load bundle options for selected cart items and added variants so they can be converted afterwards
    useEffect(() => {
        if (!selectedAppointmentStoreId) return;
        const storeId = selectedAppointmentStoreId;
        for (const item of cartItems) {
            if (!selectedCartItemIds.has(item.id) || item.bundleId || bundleFetchRequestedRef.current.has(item.id)) continue;
            bundleFetchRequestedRef.current.add(item.id);
            fetchBundlesForVariant(item.treatmentVariant.id, storeId).then((bundles) => {
                setCartItemAvailableBundles((prev) => new Map(prev).set(item.id, bundles));
            });
        }
        for (const v of newVariants) {
            if (v.availableBundles || bundleFetchRequestedRef.current.has(v.id)) continue;
            bundleFetchRequestedRef.current.add(v.id);
            fetchBundlesForVariant(v.treatmentVariantId, storeId).then((bundles) => {
                if (bundles.length === 0) return;
                setNewVariants((prev) => prev.map((nv) => (nv.id === v.id ? { ...nv, availableBundles: bundles } : nv)));
            });
        }
    }, [cartItems, selectedCartItemIds, newVariants, selectedAppointmentStoreId]);
    const hasSelection = selectedCartItemIds.size > 0 || newVariants.length > 0;

    return (
        <>
            <DialogHeader>
                <DialogTitle>Leistungen dokumentieren (Schritt 1)</DialogTitle>
                <DialogDescription>
                    Wähle Termin und durchgeführte Leistungen aus, damit die korrekte Abrechnung erstellt werden kann.
                </DialogDescription>
            </DialogHeader>

            <ScrollArea className="flex-1 px-1 -mx-1 overflow-y-scroll">
                <div className="py-4 space-y-6 pr-4">
                    <PractitionerBadge />
                    <div>
                        <Label>Termin *</Label>
                        <Select
                            value={selectedAppointmentId || ''}
                            onValueChange={(value) => setSelectedAppointmentId(value || null)}
                            disabled={isMutating}
                        >
                            <SelectTrigger className="mt-2">
                                <SelectValue placeholder="Termin auswählen..." />
                            </SelectTrigger>
                            <SelectContent>
                                {filteredAppointments.map((appointment) => (
                                    <SelectItem key={appointment.id} value={appointment.id}>
                                        {formatAppointmentOption(appointment)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {selectedAppointmentId && !isLoadingDetails && (
                        <>
                            <div>
                                <Separator className="my-2" />
                                <Label className="text-base font-medium">Warenkorb-Positionen</Label>
                                <p className="text-sm text-muted-foreground mt-1 mb-3">
                                    Markieren Sie die durchgeführten Leistungen.
                                </p>

                                {cartItems.length > 0 && (
                                    <div className="space-y-2 mb-3">
                                        {cartItems.map((item) => {
                                            const isSelected = selectedCartItemIds.has(item.id);
                                            const override = cartItemBundleOverrides.get(item.id);
                                            const bookedBundle = item.bundleId && !item.isBundleRedemption ? item.bundle : null;
                                            const isBookedRedemption = !!item.bundleId && item.isBundleRedemption;
                                            const availableBundles = cartItemAvailableBundles.get(item.id) ?? [];
                                            const canConvert = isSelected && !item.bundleId && availableBundles.length > 0;
                                            return (
                                            <div
                                                key={item.id}
                                                className={cn(
                                                    "flex items-center gap-3 p-3 border rounded-lg transition-colors",
                                                    isSelected ? "border-green-200 bg-green-50" : "hover:bg-muted/50"
                                                )}
                                            >
                                <Checkbox
                                    checked={isSelected}
                                    onCheckedChange={() => toggleCartItem(item.id)}
                                    disabled={isMutating || noTreatmentPerformed}
                                />
                                                <label
                                                    className={cn("flex-1 cursor-pointer", noTreatmentPerformed && "opacity-50 cursor-not-allowed")}
                                                    onClick={() => !noTreatmentPerformed && toggleCartItem(item.id)}
                                                >
                                                    <span className="text-sm font-medium">
                                                        {item.treatmentVariant.name}
                                                    </span>
                                                    {item.quantity > 1 && (
                                                        <span className="text-sm text-muted-foreground ml-2">
                                                            x{item.quantity}
                                                        </span>
                                                    )}
                                                    {(() => {
                                                        if (isBookedRedemption) return null;
                                                        if (override?.bundleTotalPrice != null && override.bundleTotalPrice > 0) {
                                                            return <span className="text-xs text-muted-foreground ml-2">{formatDecimalAsEuro(override.bundleTotalPrice)}</span>;
                                                        }
                                                        if (bookedBundle) {
                                                            return <span className="text-xs text-muted-foreground ml-2">{formatDecimalAsEuro(Number(bookedBundle.unitPriceGross) * bookedBundle.unitQuantity)}</span>;
                                                        }
                                                        if (item.treatmentVariant.priceCents > 0) {
                                                            return <span className="text-xs text-muted-foreground ml-2">{formatCentsAsEuro(item.treatmentVariant.priceCents)}</span>;
                                                        }
                                                        return null;
                                                    })()}
                                                </label>
                                                {isBookedRedemption && (
                                                    <Badge variant="secondary" className="gap-1">
                                                        <Gift className="h-3 w-3" />
                                                        Bundle Einlösung
                                                    </Badge>
                                                )}
                                                {(bookedBundle || (isSelected && override?.bundleId)) && (
                                                    <Badge variant="secondary" className="gap-1">
                                                        <Gift className="h-3 w-3" />
                                                        Bundle
                                                    </Badge>
                                                )}
                                                {canConvert && (
                                                    <BundleToggle
                                                        bundles={availableBundles}
                                                        selectedBundleId={override?.bundleId}
                                                        onChange={(bundle) => setCartItemBundle(item.id, bundle)}
                                                        disabled={isMutating}
                                                    />
                                                )}
                                                {isSelected && (
                                                    <Check className="h-4 w-4 text-green-600 shrink-0" />
                                                )}
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-7 w-7 shrink-0"
                                                    onClick={() => handleRemoveCartItem(item.id)}
                                                    disabled={isMutating}
                                                >
                                                    <X className="h-3.5 w-3.5 text-muted-foreground hover:text-red-500" />
                                                </Button>
                                            </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {cartItems.length === 0 && (
                                    <p className="text-sm text-muted-foreground text-center py-3 mb-3">
                                        Keine Warenkorb-Positionen vorhanden.
                                    </p>
                                )}

                                {newVariants.length > 0 && (
                                    <div className="space-y-2 mb-3">
                                        {newVariants.map((variant) => (
                                            <div
                                                key={variant.id}
                                                className="flex items-center gap-3 p-3 border border-green-200 bg-green-50 rounded-lg"
                                            >
                                                <Checkbox checked={true} disabled />
                                                <div className="flex-1 min-w-0">
                                                    <span className="text-sm font-medium">{variant.name}</span>
                                                    {variant.bundleId && variant.bundleTotalPrice != null && variant.bundleTotalPrice > 0 ? (
                                                        <span className="text-xs text-muted-foreground ml-2">{formatDecimalAsEuro(variant.bundleTotalPrice)}</span>
                                                    ) : variant.priceCents != null && variant.priceCents > 0 ? (
                                                        <span className="text-xs text-muted-foreground ml-2">{formatCentsAsEuro(variant.priceCents)}</span>
                                                    ) : null}
                                                </div>
                                                <Badge variant="secondary" className="text-[10px]">neu</Badge>
                                                {variant.bundleId && (
                                                    <Badge variant="secondary" className="gap-1">
                                                        <Gift className="h-3 w-3" />
                                                        Bundle
                                                    </Badge>
                                                )}
                                                {variant.availableBundles && variant.availableBundles.length > 0 && (
                                                    <BundleToggle
                                                        bundles={variant.availableBundles}
                                                        selectedBundleId={variant.bundleId}
                                                        onChange={(bundle) => setNewVariantBundle(variant.id, bundle)}
                                                        disabled={isMutating}
                                                    />
                                                )}
                                                <Check className="h-4 w-4 text-green-600 shrink-0" />
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-7 w-7 shrink-0"
                                                    onClick={() => handleRemoveNewVariant(variant.id)}
                                                    disabled={isMutating}
                                                >
                                                    <X className="h-3.5 w-3.5 text-muted-foreground hover:text-red-500" />
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {!noTreatmentPerformed && (
                                    <AutoComplete
                                        selectedValue={variantSelectedValue}
                                        onSelectedValueChange={(value) => {
                                            setVariantSelectedValue(value);
                                            handleVariantSelect(value);
                                        }}
                                        searchValue={variantSearchValue}
                                        onSearchValueChange={setVariantSearchValue}
                                        items={variantOptions}
                                        placeholder="Leistung hinzufügen..."
                                        emptyMessage="Keine Leistung gefunden"
                                        isLoading={isSearchingVariants}
                                        resetSearchOnSelectItem
                                    />
                                )}

                                <div className="mt-4 pt-3 border-t">
                                    <div
                                        className={cn(
                                            "p-3 rounded-lg border border-red-200 bg-red-50",
                                            noTreatmentPerformed && "ring-2 ring-red-300"
                                        )}
                                    >
                                        <div className="flex items-center gap-3">
                                            <Checkbox
                                                checked={noTreatmentPerformed}
                                                onCheckedChange={(checked) => {
                                                    if (checked && hasSelection) return;
                                                    setNoTreatment(!!checked);
                                                }}
                                                disabled={isMutating || hasSelection}
                                            />
                                            <label
                                                className={cn(
                                                    "text-sm text-red-700 cursor-pointer",
                                                    (isMutating || hasSelection) && "opacity-50 cursor-not-allowed"
                                                )}
                                                onClick={() => {
                                                    if (isMutating || hasSelection) return;
                                                    setNoTreatment(!noTreatmentPerformed);
                                                }}
                                            >
                                                Keine Behandlung durchgeführt
                                            </label>
                                        </div>

                                        {noTreatmentPerformed && (
                                            <div className="mt-3 space-y-1">
                                                <Label className="text-sm text-red-700">Grund *</Label>
                                                <Select
                                                    value={noTreatmentReason ?? ''}
                                                    onValueChange={(value) => setNoTreatmentReason(value as MedicalRecordNoTreatmentReason)}
                                                    disabled={isMutating}
                                                >
                                                    <SelectTrigger className={cn("bg-white", !noTreatmentReason && "border-red-400")}>
                                                        <SelectValue placeholder="Grund auswählen..." />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {NO_TREATMENT_REASON_OPTIONS.map(([value, label]) => (
                                                            <SelectItem key={value} value={value}>
                                                                {label}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="mt-4 pt-3 border-t">
                                <div className="flex items-center gap-2 mb-3">
                                    <ShoppingBag className="h-4 w-4 text-muted-foreground" />
                                    <Label className="text-base font-medium">Produkte</Label>
                                </div>
                                <p className="text-sm text-muted-foreground mb-3">
                                    Dokumentierte Produkte (optional).
                                </p>

                                {selectedProducts.length > 0 && (
                                    <div className="space-y-2 mb-3">
                                        {selectedProducts.map((product) => (
                                            <div
                                                key={product.physicalProductId}
                                                className="flex items-center gap-3 p-3 border border-green-200 bg-green-50 rounded-lg"
                                            >
                                                <ShoppingBag className="h-4 w-4 text-green-600 shrink-0" />
                                                <div className="flex-1 min-w-0">
                                                    <span className="text-sm font-medium">{product.name}</span>
                                                    {product.unitPriceGross != null && product.unitPriceGross > 0 && (
                                                        <span className="text-xs text-muted-foreground ml-2">{formatDecimalAsEuro(product.unitPriceGross)}</span>
                                                    )}
                                                </div>
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-7 w-7 shrink-0"
                                                    onClick={() => handleRemoveProduct(product.physicalProductId)}
                                                    disabled={isMutating}
                                                >
                                                    <X className="h-3.5 w-3.5 text-muted-foreground hover:text-red-500" />
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <AutoComplete
                                    selectedValue={productSelectedValue}
                                    onSelectedValueChange={(value) => {
                                        setProductSelectedValue(value);
                                        handleProductSelect(value);
                                    }}
                                    searchValue={productSearchValue}
                                    onSearchValueChange={setProductSearchValue}
                                    items={productOptions}
                                    placeholder="Produkt hinzufügen..."
                                    emptyMessage="Kein Produkt gefunden"
                                    isLoading={isSearchingProducts}
                                    resetSearchOnSelectItem
                                />
                            </div>

                        </>
                    )}

                    {selectedAppointmentId && isLoadingDetails && (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                            <span className="ml-2 text-sm text-muted-foreground">Termindetails werden geladen...</span>
                        </div>
                    )}
                </div>
            </ScrollArea>

            <DialogFooter className="pt-4 border-t">
                <Button
                    type="button"
                    variant="outline"
                    onClick={onCancel}
                    disabled={isMutating}
                >
                    Abbrechen
                </Button>
                <Button
                    type="button"
                    onClick={handleSave}
                    disabled={
                        isMutating ||
                        (!noTreatmentPerformed && !hasSelection) ||
                        (noTreatmentPerformed && !noTreatmentReason)
                    }
                >
                    {(saveAttribution.isPending || updateAttribution.isPending) && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Speichern
                </Button>
            </DialogFooter>
        </>
    );
};

// ============================================
// Medication Row Fields (sub-component with local AutoComplete state)
// ============================================

type MedicamentOption = { id: string; name: string; quickAccess: boolean; unit: { id: string; name: string } | null; regionIds: string[] };
type UnitOption = { id: string; name: string };
type RegionOption = { id: string; name: string };

const MedicationRowFields = ({
    med,
    medicaments,
    regions,
    units,
    onUpdate,
    disabled,
}: {
    med: Medication;
    medicaments: MedicamentOption[];
    regions: RegionOption[];
    units: UnitOption[];
    onUpdate: (id: string, field: keyof Medication, value: string | number) => void;
    disabled: boolean;
}) => {
    const [medSearch, setMedSearch] = useState('');
    const [regionSearch, setRegionSearch] = useState('');

    const selectedMedicament = medicaments.find((m) => m.id === med.medicamentId);

    const allowedRegions = useMemo(() => {
        if (!selectedMedicament || selectedMedicament.regionIds.length === 0) return regions;
        const regionMap = new Map(regions.map((r) => [r.id, r]));
        return selectedMedicament.regionIds
            .map((id) => regionMap.get(id))
            .filter((r): r is RegionOption => !!r);
    }, [selectedMedicament, regions]);

    useEffect(() => {
        if (selectedMedicament?.unit?.id && !med.medicationUnitId) {
            onUpdate(med.id, 'medicationUnitId', selectedMedicament.unit.id);
        }
    }, [selectedMedicament?.unit?.id]);

    useEffect(() => {
        if (!selectedMedicament) return;
        if (selectedMedicament.regionIds.length === 0) return;

        if (med.regionId && !selectedMedicament.regionIds.includes(med.regionId)) {
            onUpdate(med.id, 'regionId', '');
        }

        if (allowedRegions.length === 1 && med.regionId !== allowedRegions[0].id) {
            onUpdate(med.id, 'regionId', allowedRegions[0].id);
        }
    }, [selectedMedicament?.id, allowedRegions]);

    const medicamentItems = useMemo(() =>
        medicaments
            .filter((m) => !medSearch || m.name.toLowerCase().includes(medSearch.toLowerCase()))
            .map((m) => ({ value: m.id, label: m.name })),
        [medicaments, medSearch]
    );

    const regionItems = useMemo(() =>
        allowedRegions
            .filter((r) => !regionSearch || r.name.toLowerCase().includes(regionSearch.toLowerCase()))
            .map((r) => ({ value: r.id, label: r.name })),
        [allowedRegions, regionSearch]
    );

    return (
        <div className="grid grid-cols-[1fr_7.5rem_5.5rem_1fr_1fr] gap-3 items-center min-w-0">
            <div className="min-w-0">
                <AutoComplete
                    selectedValue={med.medicamentId}
                    onSelectedValueChange={(value) => {
                        onUpdate(med.id, 'medicamentId', value);
                        const newMed = medicaments.find((m) => m.id === value);
                        if (newMed?.unit?.id) {
                            onUpdate(med.id, 'medicationUnitId', newMed.unit.id);
                        }
                    }}
                    searchValue={medSearch}
                    onSearchValueChange={setMedSearch}
                    items={medicamentItems}
                    placeholder="Medikament..."
                    emptyMessage="Kein Medikament gefunden"
                />
            </div>
            <div className="flex items-center gap-0.5">
                <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-9 w-7 shrink-0"
                    onClick={() => onUpdate(med.id, 'medicationQuantity', Math.max(0, med.medicationQuantity - 1))}
                    disabled={disabled || med.medicationQuantity <= 0}
                >
                    <Minus className="h-3 w-3" />
                </Button>
                <Input
                    type="number"
                    min="0"
                    step="any"
                    value={med.medicationQuantity || ''}
                    onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === '') {
                            onUpdate(med.id, 'medicationQuantity', 0);
                        } else {
                            onUpdate(med.id, 'medicationQuantity', parseFloat(raw) || 0);
                        }
                    }}
                    className="h-9 w-14 text-center px-1 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    disabled={disabled}
                />
                <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-9 w-7 shrink-0"
                    onClick={() => onUpdate(med.id, 'medicationQuantity', med.medicationQuantity + 1)}
                    disabled={disabled}
                >
                    <Plus className="h-3 w-3" />
                </Button>
            </div>
            <Select
                value={med.medicationUnitId || undefined}
                onValueChange={(value) => onUpdate(med.id, 'medicationUnitId', value)}
                disabled={disabled}
            >
                <SelectTrigger className="h-9 w-full">
                    <SelectValue placeholder="Einheit" />
                </SelectTrigger>
                <SelectContent>
                    {units.map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                </SelectContent>
            </Select>
            <div className="min-w-0">
                <AutoComplete
                    selectedValue={med.regionId}
                    onSelectedValueChange={(value) => onUpdate(med.id, 'regionId', value)}
                    searchValue={regionSearch}
                    onSearchValueChange={setRegionSearch}
                    items={regionItems}
                    placeholder="Region..."
                    emptyMessage="Keine Region gefunden"
                />
            </div>
            <Input
                value={med.notes}
                onChange={(e) => onUpdate(med.id, 'notes', e.target.value)}
                placeholder="Optional"
                disabled={disabled}
                className="truncate"
            />
        </div>
    );
};

// ============================================
// Step 2: Medical Documentation Step
// ============================================

const MedicalDocumentationStep = ({
    customerId,
    treatmentId,
    treatmentDateTime,
    initialData,
    isEditMode,
    isReEdit,
    storeId,
    onComplete,
    onCancel,
}: MedicalDocumentationStepProps) => {
    const [diagnosis, setDiagnosis] = useState(initialData?.diagnosis ?? '');
    const [medications, setMedications] = useState<Medication[]>(() => {
        if (!initialData?.medications?.length) {
            return [];
        }
        return initialData.medications.map((m) => ({
            id: m.id,
            medicamentId: m.medicamentId ?? m.medicament?.id ?? '',
            regionId: m.regionId ?? m.region?.id ?? '',
            medicationQuantity: Number(m.medicationQuantity) || 0,
            medicationUnitId: m.medicationUnitId ?? m.medicament?.unit?.id ?? '',
            notes: m.notes ?? '',
        }));
    });

    const [snippetSearch, setSnippetSearch] = useState('');
    const [selectedSnippetIds, setSelectedSnippetIds] = useState<Set<string>>(new Set());
    const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
    const expandedSnippetRef = useRef<HTMLDivElement>(null);
    const [notesError, setNotesError] = useState(false);
    const [consentsAlreadyInserted, setConsentsAlreadyInserted] = useState(false);

    const [mainIcdId, setMainIcdId] = useState<string | null>(initialData?.mainIcdId ?? null);
    const [mainIcdDisplay, setMainIcdDisplay] = useState(initialData?.mainIcdDisplay ?? '');
    const [mainIcdSearch, setMainIcdSearch] = useState('');
    const [secondaryIcdId, setSecondaryIcdId] = useState<string | null>(initialData?.secondaryIcdId ?? null);
    const [secondaryIcdDisplay, setSecondaryIcdDisplay] = useState(initialData?.secondaryIcdDisplay ?? '');
    const [secondaryIcdSearch, setSecondaryIcdSearch] = useState('');
    const [mainIcdOpen, setMainIcdOpen] = useState(false);
    const [secondaryIcdOpen, setSecondaryIcdOpen] = useState(false);

    const notesEditorRef = useRef<PlateEditorRef>(null);
    const utils = trpc.useUtils();

    const { data: mainIcdResults } = trpc.medicalRecord.searchIcdCodes.useQuery(
        { search: mainIcdSearch },
        { enabled: mainIcdSearch.length >= 2 }
    );
    const { data: secondaryIcdResults } = trpc.medicalRecord.searchIcdCodes.useQuery(
        { search: secondaryIcdSearch },
        { enabled: secondaryIcdSearch.length >= 2 }
    );

    const { data: treatmentTextSnippets } = trpc.medicalRecord.getTreatmentTextSnippets.useQuery(
        { storeId: storeId ?? undefined }
    );
    const { data: mandatoryConsentPairs } = trpc.medicationConfig.getMandatoryTextPairs.useQuery();
    const [consentSelections, setConsentSelections] = useState<Record<string, 'positive' | 'negative' | 'notApplicable'>>({});
    const [allNotApplicable, setAllNotApplicable] = useState(false);
    const { data: customer } = trpc.customers.getOne.useQuery({ id: customerId });
    const customerGender = customer?.data?.gender ?? null;

    const isEffectivelyRequired = (pair: { required: boolean; onlyRequiredForFemales: boolean }) => {
        if (pair.onlyRequiredForFemales) {
            return customerGender === 'FEMALE';
        }
        return pair.required;
    };

    const sortedConsentPairs = useMemo(() => {
        const pairs = mandatoryConsentPairs ?? [];
        const required = pairs.filter((p) => p.required && !p.onlyRequiredForFemales);
        const notRequired = pairs.filter((p) => !p.required && !p.onlyRequiredForFemales);
        const femalesOnly = pairs.filter((p) => p.onlyRequiredForFemales);
        return [...required, ...notRequired, ...femalesOnly];
    }, [mandatoryConsentPairs]);

    useEffect(() => {
        if (!mandatoryConsentPairs || !customerGender) return;
        setConsentSelections((prev) => {
            const next = { ...prev };
            for (const pair of mandatoryConsentPairs) {
                if (pair.onlyRequiredForFemales && customerGender !== 'FEMALE' && !next[pair.id]) {
                    next[pair.id] = 'notApplicable';
                }
            }
            return next;
        });
    }, [mandatoryConsentPairs, customerGender]);

    useEffect(() => {
        if (selectedCategoryId && expandedSnippetRef.current) {
            expandedSnippetRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, [selectedCategoryId]);

    const { data: textCategories = [] } = trpc.medicationConfig.getTextCategories.useQuery();
    const { data: medicaments } = trpc.medicalRecord.getMedicaments.useQuery();
    const { data: treatmentRegions } = trpc.medicalRecord.getTreatmentRegions.useQuery();
    const { data: allUnits } = trpc.medicationConfig.getAllUnits.useQuery();

    const sortedCategories = useMemo(() => {
        return [...textCategories].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
    }, [textCategories]);

    const filteredSnippets = useMemo(() => {
        if (!treatmentTextSnippets) return [];
        if (!snippetSearch.trim()) return treatmentTextSnippets;
        const term = snippetSearch.toLowerCase();
        return treatmentTextSnippets.filter(
            (s) => s.name.toLowerCase().includes(term) || s.tags.some((t) => t.toLowerCase().includes(term))
        );
    }, [treatmentTextSnippets, snippetSearch]);

    const toggleSnippet = (id: string) => {
        setSelectedSnippetIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const allConsentPairsSelected = (mandatoryConsentPairs ?? []).length === 0 ||
        Object.keys(consentSelections).length >= (mandatoryConsentPairs ?? []).length;
    const insertableConsentCount = Object.values(consentSelections).filter(
        (sel) => sel === 'positive' || sel === 'negative'
    ).length;

    const handleInsertSnippets = () => {
        const htmlParts: string[] = [];

        if (!consentsAlreadyInserted) {
            const requiredPairs = (mandatoryConsentPairs ?? []).filter((p) => p.required);
            const optionalPairs = (mandatoryConsentPairs ?? []).filter((p) => !p.required);

            const allRequiredPositive = requiredPairs.length > 0 && requiredPairs.every((p) => {
                return consentSelections[p.id] === 'positive';
            });

            if (allRequiredPositive) {
                htmlParts.push('<p>Pat. volljährig, einwilligungsfähig, sprachlich orientiert; kein Anhalt für unrealistische Erwartungshaltung/BDD. Anamnese unauffällig (keine relevanten Vorerkrankungen, keine relevante Medikation, keine Allergien) keine Kontraindikationen. Aufklärung mündlich und schriftlich erfolgt über Ablauf, Ergebnis, allgemeine und behandlungsspezifische Risiken (sh. SOP), Alternativen. Fragen beantwortet, Verständnis bestätigt. Wirtschaftliche Aufklärung erfolgt: Selbstzahlerleistung, Gesamt- und Folgekosten dargelegt. Pat. wurde ausdrücklich auf das Recht auf Bedenkzeit hingewiesen und hat hierauf aus eigenem Antrieb ausdrücklich verzichtet, sofortige Behandlung gewünscht. Einwilligung unterschrieben. Behandlung komplikationslos unter Einhaltung der geltenden Hygiene- und Desinfektionsstandards durchgeführt.</p>');
                for (const pair of optionalPairs) {
                    const sel = consentSelections[pair.id];
                    if (sel === 'positive') htmlParts.push(pair.positiveText.content);
                    else if (sel === 'negative') htmlParts.push(pair.negativeText.content);
                }
            } else {
                for (const pair of (mandatoryConsentPairs ?? [])) {
                    const sel = consentSelections[pair.id];
                    if (sel === 'positive') htmlParts.push(pair.positiveText.content);
                    else if (sel === 'negative') htmlParts.push(pair.negativeText.content);
                }
            }

            setConsentsAlreadyInserted(true);
        }

        if (treatmentTextSnippets && selectedSnippetIds.size > 0) {
            const selectedSnippets = treatmentTextSnippets.filter((s) => selectedSnippetIds.has(s.id));
            htmlParts.push(...selectedSnippets.map((s) => s.content));
        }

        if (htmlParts.length > 0) {
            notesEditorRef.current?.appendHtml(htmlParts.join('<p>&nbsp;</p>'));
        }
        setSelectedSnippetIds(new Set());
        setSnippetSearch('');
    };

    const completeMedicalDocumentation = trpc.medicalRecord.completeMedicalDocumentationStep.useMutation({
        onSuccess: () => {
            utils.medicalRecord.getByCustomerId.invalidate({ customerId });
            utils.medicalRecord.getIncompleteTreatment.invalidate({ customerId });
            utils.medicalRecord.getTreatmentById.invalidate({ customerId, treatmentId });
            utils.qm.getMyOutstandingDocumentation.invalidate();
            toast.success('Behandlung wurde dokumentiert');
            onComplete();
        },
        onError: (error) => {
            toast.error(`Fehler beim Speichern: ${error.message}`);
        },
    });

    const updateMedicalDocumentation = trpc.medicalRecord.updateMedicalDocumentationStep.useMutation({
        onSuccess: () => {
            utils.medicalRecord.getByCustomerId.invalidate({ customerId });
            utils.medicalRecord.getIncompleteTreatment.invalidate({ customerId });
            utils.medicalRecord.getTreatmentById.invalidate({ customerId, treatmentId });
            utils.qm.getMyOutstandingDocumentation.invalidate();
            toast.success('Behandlung wurde aktualisiert');
            onComplete();
        },
        onError: (error) => {
            toast.error(`Fehler beim Speichern: ${error.message}`);
        },
    });

    const addMedication = () => {
        setMedications([...medications, createEmptyMedication()]);
    };

    const addQuickAccessMedication = (m: MedicamentOption) => {
        setMedications((prev) => [...prev, {
            id: crypto.randomUUID(),
            medicamentId: m.id,
            medicationQuantity: 1,
            medicationUnitId: m.unit?.id ?? '',
            regionId: m.regionIds.length === 1 ? m.regionIds[0] : '',
            notes: '',
        }]);
    };

    const removeMedication = (id: string) => {
        setMedications(medications.filter((m) => m.id !== id));
    };

    const updateMedication = (id: string, field: keyof Medication, value: string | number) => {
        setMedications((prev) =>
            prev.map((m) => (m.id === id ? { ...m, [field]: value } : m))
        );
    };

    const validateMedications = (): boolean => {
        for (const med of medications) {
            if (!med.medicamentId) {
                toast.error('Bitte wählen Sie für alle Medikamente ein Medikament aus');
                return false;
            }
            if (!med.regionId) {
                toast.error('Bitte wählen Sie für alle Medikamente eine Region aus');
                return false;
            }
            if (!med.medicationQuantity || med.medicationQuantity <= 0) {
                toast.error('Bitte geben Sie eine gültige Menge (> 0) für alle Medikamente ein');
                return false;
            }
            if (!med.medicationUnitId) {
                toast.error('Bitte wählen Sie eine Einheit für alle Medikamente aus');
                return false;
            }
        }
        return true;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateMedications()) {
            return;
        }

        const consentPairsCount = (mandatoryConsentPairs ?? []).length;
        if (!isReEdit && consentPairsCount > 0 && Object.keys(consentSelections).length < consentPairsCount) {
            toast.error('Bitte alle Einwilligungen auswählen');
            return;
        }

        const notesHtml = notesEditorRef.current?.getHtml() || '';
        const strippedNotes = notesHtml.replace(/<[^>]*>/g, '').trim();
        if (!strippedNotes) {
            setNotesError(true);
            toast.error('Bitte geben Sie Notizen ein');
            return;
        }
        setNotesError(false);

        const payload = {
            treatmentId,
            treatmentDateLocal: treatmentDateTime.toJson(),
            diagnosis: diagnosis.trim() || undefined,
            mainIcdId: mainIcdId || undefined,
            secondaryIcdId: secondaryIcdId || undefined,
            notes: notesHtml || undefined,
            medications: medications.map((m) => ({
                medicamentId: m.medicamentId,
                regionId: m.regionId,
                medicationQuantity: m.medicationQuantity,
                medicationUnitId: m.medicationUnitId || undefined,
                notes: m.notes.trim() || undefined,
            })),
        };

        if (isEditMode) {
            updateMedicalDocumentation.mutate(payload);
        } else {
            completeMedicalDocumentation.mutate(payload);
        }
    };

    const isPending = completeMedicalDocumentation.isPending || updateMedicalDocumentation.isPending;

    return (
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
            <DialogHeader>
                <DialogTitle>Medizinische Dokumentation (Schritt 2)</DialogTitle>
                <DialogDescription>
                    Detaillierte medizinische Dokumentation der Diagnose, durchgeführten Leistungen und Medikation.
                </DialogDescription>
            </DialogHeader>

            <ScrollArea className="flex-1 px-1 -mx-1 overflow-y-scroll">
                <div className="py-4 space-y-6 pr-4">
                    <PractitionerBadge />
                    <div className="grid grid-cols-[1fr_1fr_1fr] gap-3">
                        <div className="col-span-1">
                            <Label htmlFor="diagnosis">Diagnose</Label>
                            <Input
                                id="diagnosis"
                                value={diagnosis}
                                onChange={(e) => setDiagnosis(e.target.value)}
                                placeholder="Diagnose eingeben..."
                                className="mt-2 bg-white"
                                disabled={isPending}
                            />
                        </div>
                        <div className="col-span-1 relative">
                            <Label>Haupt-ICD</Label>
                            <Popover open={mainIcdOpen} onOpenChange={setMainIcdOpen}>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        role="combobox"
                                        className="w-full mt-2 justify-start font-normal truncate bg-white"
                                        disabled={isPending}
                                    >
                                        <span className="truncate">
                                            {mainIcdDisplay || 'ICD suchen...'}
                                        </span>
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-80 p-0" align="start">
                                    <div className="p-2 border-b">
                                        <Input
                                            value={mainIcdSearch}
                                            onChange={(e) => setMainIcdSearch(e.target.value)}
                                            placeholder="ICD-Code oder Name suchen..."
                                            className="h-8"
                                            autoFocus
                                        />
                                    </div>
                                    <div className="max-h-48 overflow-y-auto">
                                        {mainIcdSearch.length < 2 ? (
                                            <p className="p-2 text-xs text-muted-foreground">Mind. 2 Zeichen eingeben...</p>
                                        ) : !mainIcdResults?.length ? (
                                            <p className="p-2 text-xs text-muted-foreground">Keine Ergebnisse</p>
                                        ) : (
                                            mainIcdResults.map((icd) => (
                                                <button
                                                    key={icd.id}
                                                    type="button"
                                                    className={cn(
                                                        "w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2",
                                                        mainIcdId === icd.id && "bg-muted"
                                                    )}
                                                    onClick={() => {
                                                        setMainIcdId(icd.id);
                                                        setMainIcdDisplay(`${icd.icdNumber} ${icd.name}`);
                                                        setMainIcdOpen(false);
                                                        setMainIcdSearch('');
                                                    }}
                                                >
                                                    {mainIcdId === icd.id && <Check className="h-3 w-3 shrink-0" />}
                                                    <span className="truncate">{icd.icdNumber} - {icd.name}</span>
                                                </button>
                                            ))
                                        )}
                                    </div>
                                    {mainIcdId && (
                                        <div className="border-t p-1">
                                            <button
                                                type="button"
                                                className="w-full text-left px-3 py-1.5 text-xs text-muted-foreground hover:bg-muted rounded"
                                                onClick={() => {
                                                    setMainIcdId(null);
                                                    setMainIcdDisplay('');
                                                    setMainIcdOpen(false);
                                                    setMainIcdSearch('');
                                                }}
                                            >
                                                Auswahl entfernen
                                            </button>
                                        </div>
                                    )}
                                </PopoverContent>
                            </Popover>
                        </div>
                        <div className="col-span-1 relative">
                            <Label>Neben-ICD</Label>
                            <Popover open={secondaryIcdOpen} onOpenChange={setSecondaryIcdOpen}>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        role="combobox"
                                        className="w-full mt-2 justify-start font-normal truncate bg-white"
                                        disabled={isPending}
                                    >
                                        <span className="truncate">
                                            {secondaryIcdDisplay || 'ICD suchen...'}
                                        </span>
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-80 p-0" align="start">
                                    <div className="p-2 border-b">
                                        <Input
                                            value={secondaryIcdSearch}
                                            onChange={(e) => setSecondaryIcdSearch(e.target.value)}
                                            placeholder="ICD-Code oder Name suchen..."
                                            className="h-8"
                                            autoFocus
                                        />
                                    </div>
                                    <div className="max-h-48 overflow-y-auto">
                                        {secondaryIcdSearch.length < 2 ? (
                                            <p className="p-2 text-xs text-muted-foreground">Mind. 2 Zeichen eingeben...</p>
                                        ) : !secondaryIcdResults?.length ? (
                                            <p className="p-2 text-xs text-muted-foreground">Keine Ergebnisse</p>
                                        ) : (
                                            secondaryIcdResults.map((icd) => (
                                                <button
                                                    key={icd.id}
                                                    type="button"
                                                    className={cn(
                                                        "w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2",
                                                        secondaryIcdId === icd.id && "bg-muted"
                                                    )}
                                                    onClick={() => {
                                                        setSecondaryIcdId(icd.id);
                                                        setSecondaryIcdDisplay(`${icd.icdNumber} ${icd.name}`);
                                                        setSecondaryIcdOpen(false);
                                                        setSecondaryIcdSearch('');
                                                    }}
                                                >
                                                    {secondaryIcdId === icd.id && <Check className="h-3 w-3 shrink-0" />}
                                                    <span className="truncate">{icd.icdNumber} - {icd.name}</span>
                                                </button>
                                            ))
                                        )}
                                    </div>
                                    {secondaryIcdId && (
                                        <div className="border-t p-1">
                                            <button
                                                type="button"
                                                className="w-full text-left px-3 py-1.5 text-xs text-muted-foreground hover:bg-muted rounded"
                                                onClick={() => {
                                                    setSecondaryIcdId(null);
                                                    setSecondaryIcdDisplay('');
                                                    setSecondaryIcdOpen(false);
                                                    setSecondaryIcdSearch('');
                                                }}
                                            >
                                                Auswahl entfernen
                                            </button>
                                        </div>
                                    )}
                                </PopoverContent>
                            </Popover>
                        </div>
                    </div>

                    <div className="border-2 rounded-lg p-4 bg-muted/40 space-y-4">
                        {(mandatoryConsentPairs ?? []).length > 0 && (
                            <div>
                                <div className="flex items-center justify-between">
                                    <Label className="text-base font-medium">Einwilligungen</Label>
                                    <div className="flex flex-col items-end">
                                        <Button
                                            type="button"
                                            variant={allNotApplicable ? "default" : "outline"}
                                            size="sm"
                                            className={cn(
                                                "h-7 text-xs",
                                                allNotApplicable && "bg-gray-500 hover:bg-gray-600"
                                            )}
                                            onClick={() => {
                                                if (allNotApplicable) {
                                                    setConsentSelections({});
                                                    setAllNotApplicable(false);
                                                } else {
                                                    const allNA: Record<string, 'notApplicable'> = {};
                                                    for (const pair of (mandatoryConsentPairs ?? [])) {
                                                        allNA[pair.id] = 'notApplicable';
                                                    }
                                                    setConsentSelections(allNA);
                                                    setAllNotApplicable(true);
                                                }
                                                setConsentsAlreadyInserted(false);
                                            }}
                                            disabled={isPending}
                                        >
                                            Alle nicht zutreffend
                                        </Button>
                                        <span className="text-[10px] text-muted-foreground mt-0.5">
                                            Bei Beratung / Keine Behandlung
                                        </span>
                                    </div>
                                </div>
                                <div className="mt-2 grid grid-cols-5 gap-3">
                                    {sortedConsentPairs.map((pair) => {
                                        const selection = consentSelections[pair.id];
                                        return (
                                            <div
                                                key={pair.id}
                                                className={cn(
                                                    "border-2 rounded-lg p-3 flex flex-col items-center gap-2 transition-colors",
                                                    !selection && "border-orange-400 bg-white",
                                                    selection === 'positive' && "border-green-500 bg-green-50",
                                                    selection === 'negative' && "border-red-500 bg-red-50",
                                                    selection === 'notApplicable' && "border-gray-400 bg-gray-100",
                                                )}
                                            >
                                                <span className="text-sm font-medium text-center flex-grow">{pair.name}</span>
                                                <div className="flex gap-2 mt-auto">
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        variant={selection === 'positive' ? 'default' : 'outline'}
                                                        className={cn(
                                                            "h-8 w-8 p-0",
                                                            selection === 'positive' && "bg-green-600 hover:bg-green-700"
                                                        )}
                                                        onClick={() => {
                                                            setConsentsAlreadyInserted(false);
                                                            if (selection === 'positive') {
                                                                setConsentSelections((prev) => {
                                                                    const next = { ...prev };
                                                                    delete next[pair.id];
                                                                    return next;
                                                                });
                                                            } else {
                                                                setConsentSelections((prev) => ({ ...prev, [pair.id]: 'positive' }));
                                                            }
                                                        }}
                                                        disabled={isPending || allNotApplicable}
                                                    >
                                                        <Check className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        variant={selection === 'negative' ? 'default' : 'outline'}
                                                        className={cn(
                                                            "h-8 w-8 p-0",
                                                            selection === 'negative' && "bg-red-600 hover:bg-red-700"
                                                        )}
                                                        onClick={() => {
                                                            setConsentsAlreadyInserted(false);
                                                            if (selection === 'negative') {
                                                                setConsentSelections((prev) => {
                                                                    const next = { ...prev };
                                                                    delete next[pair.id];
                                                                    return next;
                                                                });
                                                            } else {
                                                                setConsentSelections((prev) => ({ ...prev, [pair.id]: 'negative' }));
                                                            }
                                                        }}
                                                        disabled={isPending || allNotApplicable}
                                                    >
                                                        <X className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                                {(!pair.required || pair.onlyRequiredForFemales) && (
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        variant={selection === 'notApplicable' ? 'default' : 'outline'}
                                                        className={cn(
                                                            "h-6 w-full text-[10px]",
                                                            selection === 'notApplicable' && "bg-gray-500 hover:bg-gray-600"
                                                        )}
                                                        onClick={() => {
                                                            setConsentsAlreadyInserted(false);
                                                            if (selection === 'notApplicable') {
                                                                setConsentSelections((prev) => {
                                                                    const next = { ...prev };
                                                                    delete next[pair.id];
                                                                    return next;
                                                                });
                                                            } else {
                                                                setConsentSelections((prev) => ({ ...prev, [pair.id]: 'notApplicable' }));
                                                            }
                                                        }}
                                                        disabled={isPending || allNotApplicable}
                                                    >
                                                        Nicht zutreffend
                                                    </Button>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        <div>
                            <Label className="text-base font-medium">Textbausteine</Label>
                            <div className="mt-2 border rounded-lg bg-white">
                                <div className="relative p-2 border-b">
                                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        value={snippetSearch}
                                        onChange={(e) => setSnippetSearch(e.target.value)}
                                        placeholder="Textbaustein suchen..."
                                        className="pl-8 bg-white"
                                        disabled={isPending}
                                    />
                                </div>

                                <ScrollArea className="max-h-[240px] overflow-y-auto">
                                    {snippetSearch.trim() ? (
                                        filteredSnippets.length > 0 ? (
                                            <div className="divide-y">
                                                {filteredSnippets.map((snippet) => (
                                                    <div
                                                        key={snippet.id}
                                                        className={cn(
                                                            "px-3 py-2 cursor-pointer hover:bg-muted/50 transition-colors text-sm",
                                                            selectedSnippetIds.has(snippet.id) && "bg-green-50 text-green-800"
                                                        )}
                                                        onClick={() => toggleSnippet(snippet.id)}
                                                    >
                                                        {snippet.name}
                                                        {snippet.category && (
                                                            <span className="ml-2 text-xs text-muted-foreground">
                                                                {snippet.category.name}
                                                            </span>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-sm text-muted-foreground text-center py-4">
                                                Keine Textbausteine gefunden
                                            </p>
                                        )
                                    ) : (
                                        <div className="p-3 grid grid-cols-3 gap-2">
                                            {sortedCategories.map((cat) => {
                                                const isExpanded = selectedCategoryId === cat.id;
                                                const categorySnippets = (treatmentTextSnippets?.filter((s) => s.categoryId === cat.id) ?? [])
                                                    .sort((a, b) => (a.categoryOrder ?? 0) - (b.categoryOrder ?? 0));
                                                return (
                                                    <div key={cat.id} className="col-span-1">
                                                        <div
                                                            className={cn(
                                                                "border rounded-md px-3 py-2 text-sm cursor-pointer hover:bg-muted/50 transition-colors text-center truncate",
                                                                isExpanded && "bg-muted border-primary"
                                                            )}
                                                            style={!isExpanded && cat.color ? {
                                                                backgroundColor: `${cat.color}20`,
                                                                borderColor: cat.color,
                                                            } : undefined}
                                                            onClick={() => setSelectedCategoryId(isExpanded ? null : cat.id)}
                                                        >
                                                            {cat.name}
                                                        </div>
                                                        {isExpanded && categorySnippets.length > 0 && (
                                                            <div
                                                                ref={expandedSnippetRef}
                                                                className="mt-1 border rounded-md divide-y max-h-[120px] overflow-y-auto"
                                                            >
                                                                {categorySnippets.map((snippet) => (
                                                                    <div
                                                                        key={snippet.id}
                                                                        className={cn(
                                                                            "px-2 py-1.5 cursor-pointer hover:bg-muted/50 transition-colors text-xs",
                                                                            selectedSnippetIds.has(snippet.id) && "bg-green-50 text-green-800"
                                                                        )}
                                                                        onClick={() => toggleSnippet(snippet.id)}
                                                                    >
                                                                        {snippet.name}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                        {isExpanded && categorySnippets.length === 0 && (
                                                            <p className="mt-1 text-xs text-muted-foreground text-center py-2">
                                                                Keine Bausteine
                                                            </p>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                            {sortedCategories.length === 0 && (
                                                <p className="text-sm text-muted-foreground text-center py-4 col-span-3">
                                                    Keine Kategorien vorhanden
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </ScrollArea>

                                {(selectedSnippetIds.size > 0 || insertableConsentCount > 0 || Object.keys(consentSelections).length > 0) && (
                                    <div className="p-2 border-t">
                                        <div className="flex flex-wrap gap-1.5 mb-2">
                                            {Array.from(selectedSnippetIds).map((id) => {
                                                const snippet = treatmentTextSnippets?.find((s) => s.id === id);
                                                if (!snippet) return null;
                                                return (
                                                    <Badge
                                                        key={id}
                                                        variant="outline"
                                                        className="gap-1 pr-1 border-green-300 bg-green-50 text-green-800"
                                                    >
                                                        {snippet.name}
                                                        <button
                                                            type="button"
                                                            onClick={() => toggleSnippet(id)}
                                                            className="ml-1 rounded-full hover:bg-muted p-0.5"
                                                        >
                                                            <X className="h-3 w-3" />
                                                        </button>
                                                    </Badge>
                                                );
                                            })}
                                        </div>
                                        <Button
                                            type="button"
                                            size="sm"
                                            className="w-full bg-green-600 hover:bg-green-700 text-white"
                                            onClick={handleInsertSnippets}
                                            disabled={isPending || (!isReEdit && !allConsentPairsSelected && !consentsAlreadyInserted)}
                                        >
                                            {!isReEdit && !allConsentPairsSelected && !consentsAlreadyInserted
                                                ? 'Bitte alle Einwilligungen auswählen'
                                                : consentsAlreadyInserted
                                                    ? `Ausgewählte einfügen (${selectedSnippetIds.size})`
                                                    : `Ausgewählte einfügen (${selectedSnippetIds.size + insertableConsentCount})`}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div>
                            <Label>Notizen *</Label>
                            <div className="mt-2 bg-white rounded-md border">
                                <PlateEditor
                                    ref={notesEditorRef}
                                    initialValue={initialData?.notes || ""}
                                    placeholder="Zusätzliche Notizen..."
                                    disabled={isPending}
                                />
                            </div>
                            {notesError && (
                                <p className="text-sm text-red-600 mt-1">Notizen dürfen nicht leer sein</p>
                            )}
                        </div>
                    </div>

                    <div className="border-2 rounded-lg p-4 bg-muted/40">
                        <div className="flex items-center justify-between gap-2">
                            <Label className="text-base font-medium">Medikamente</Label>
                            {(medicaments ?? []).filter((m) => m.quickAccess).length > 0 && (
                                <div className="flex flex-wrap items-center gap-1.5">
                                    <span className="text-xs text-muted-foreground mr-1">Schnellzugriff:</span>
                                    {(medicaments ?? []).filter((m) => m.quickAccess).map((m) => (
                                        <Button
                                            key={m.id}
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            className="h-7 text-xs"
                                            onClick={() => addQuickAccessMedication(m)}
                                            disabled={isPending}
                                        >
                                            {m.name}
                                        </Button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {medications.length === 0 ? (
                            <p className="text-sm text-muted-foreground text-center py-4">
                                Noch keine Medikamente hinzugefügt
                            </p>
                        ) : (
                            <div className="mt-3">
                                <div className="grid grid-cols-[1fr_7.5rem_5.5rem_1fr_1fr] gap-3 mb-2 px-1">
                                    <span className="text-xs font-medium text-muted-foreground">Medikament *</span>
                                    <span className="text-xs font-medium text-muted-foreground">Menge *</span>
                                    <span className="text-xs font-medium text-muted-foreground">Einheit *</span>
                                    <span className="text-xs font-medium text-muted-foreground">Region *</span>
                                    <span className="text-xs font-medium text-muted-foreground">Notiz</span>
                                </div>
                                <div className="space-y-2">
                                    {medications.map((med) => (
                                        <div key={med.id} className="flex items-center gap-1">
                                            <div className="flex-1">
                                                <MedicationRowFields
                                                    med={med}
                                                    medicaments={medicaments ?? []}
                                                    regions={treatmentRegions ?? []}
                                                    units={allUnits ?? []}
                                                    onUpdate={updateMedication}
                                                    disabled={isPending}
                                                />
                                            </div>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                className="h-7 w-7 shrink-0"
                                                onClick={() => removeMedication(med.id)}
                                                disabled={isPending}
                                            >
                                                <Trash2 className="h-3.5 w-3.5 text-red-500" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="mt-3 flex justify-center">
                            <div className="flex flex-col items-center gap-1">
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="icon"
                                    className="h-8 w-8"
                                    onClick={addMedication}
                                    disabled={isPending}
                                >
                                    <Plus className="h-4 w-4" />
                                </Button>
                                <span className="text-xs text-muted-foreground">Medikament hinzufügen</span>
                            </div>
                        </div>
                    </div>
                </div>
            </ScrollArea>

            <DialogFooter className="pt-4 border-t gap-2">
                <Button
                    type="button"
                    variant="outline"
                    onClick={onCancel}
                    disabled={isPending}
                >
                    Abbrechen
                </Button>
                <Button type="submit" disabled={isPending}>
                    {(completeMedicalDocumentation.isPending || updateMedicalDocumentation.isPending) && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Speichern
                </Button>
            </DialogFooter>
        </form>
    );
};

// ============================================
// Main Dialog Wrapper
// ============================================

const AddTreatmentDialog = ({
    customerId,
    treatmentId: treatmentIdProp,
    forceNew = false,
    forceAttributionStep = false,
    preselectedAppointmentId,
    open,
    onOpenChange,
    onSuccess,
}: AddTreatmentDialogProps) => {
    const [currentStep, setCurrentStep] = useState<CurrentStep>('ATTRIBUTION_STEP');
    const [treatmentId, setTreatmentId] = useState<string | null>(null);
    const [treatmentDateTime, setTreatmentDateTime] = useState<LocalDateTime>(
        LocalDateTime.now(DEFAULT_TIMEZONE)
    );
    const [selectedAppointmentId, setSelectedAppointmentId] = useState<string | null>(null);

    const isEditMode = !!treatmentIdProp;

    const { data: incompleteTreatment } = trpc.medicalRecord.getIncompleteTreatment.useQuery(
        { customerId },
        { enabled: open && !isEditMode && !forceNew }
    );

    const { data: treatmentById, isLoading: isLoadingTreatmentById, isFetched: isTreatmentByIdFetched } =
        trpc.medicalRecord.getTreatmentById.useQuery(
            { customerId, treatmentId: treatmentIdProp! },
            { enabled: open && !!treatmentIdProp }
        );

    const { data: parentAppointments } = trpc.appointments.getByCustomerId.useQuery(
        { customerId },
        { enabled: open && !!selectedAppointmentId }
    );

    const appointmentStoreId = useMemo(() => {
        if (!selectedAppointmentId || !parentAppointments) return undefined;
        const apt = parentAppointments.find((a: any) => a.id === selectedAppointmentId);
        return apt?.store?.id ?? undefined;
    }, [selectedAppointmentId, parentAppointments]);

    const treatmentData = isEditMode ? treatmentById : incompleteTreatment;
    const treatmentNotFound = isEditMode && isTreatmentByIdFetched && !treatmentById;

    useEffect(() => {
        if (!open) {
            return;
        }

        if (isEditMode && treatmentById) {
            setTreatmentId(treatmentById.id);
            setCurrentStep(forceAttributionStep ? 'ATTRIBUTION_STEP' : 'MEDICAL_DOCUMENTATION_STEP');
            setSelectedAppointmentId(treatmentById.appointmentId ?? null);

            if (treatmentById.treatmentDateLocal) {
                const dateLocal = treatmentById.treatmentDateLocal as { localDatetime: string; zone: string };
                const restored = LocalDateTime.deserialize(dateLocal);
                setTreatmentDateTime(restored);
            }
        } else if (!isEditMode && incompleteTreatment) {
            setTreatmentId(incompleteTreatment.id);
            setCurrentStep('MEDICAL_DOCUMENTATION_STEP');
            setSelectedAppointmentId(incompleteTreatment.appointmentId ?? null);

            if (incompleteTreatment.treatmentDateLocal) {
                const dateLocal = incompleteTreatment.treatmentDateLocal as { localDatetime: string; zone: string };
                const restored = LocalDateTime.deserialize(dateLocal);
                setTreatmentDateTime(restored);
            }
        } else if (!isEditMode) {
            setCurrentStep('ATTRIBUTION_STEP');
            setTreatmentId(null);
            if (preselectedAppointmentId) {
                setSelectedAppointmentId(preselectedAppointmentId);
            }
        }
    }, [open, isEditMode, incompleteTreatment, treatmentById, preselectedAppointmentId, forceAttributionStep]);

    const resetForm = () => {
        setCurrentStep('ATTRIBUTION_STEP');
        setTreatmentId(null);
        setTreatmentDateTime(LocalDateTime.now(DEFAULT_TIMEZONE));
        setSelectedAppointmentId(null);
    };

    const handleOpenChange = (newOpen: boolean) => {
        if (!newOpen) {
            resetForm();
        }
        onOpenChange(newOpen);
    };

    const handleAttributionSaved = (_newTreatmentId: string) => {
        resetForm();
        onOpenChange(false);
        onSuccess?.();
    };

    const handleComplete = () => {
        resetForm();
        onOpenChange(false);
        onSuccess?.();
    };

    const handleBack = () => {
        setCurrentStep('ATTRIBUTION_STEP');
    };

    const initialCartItemIds = useMemo(() => {
        if (!treatmentData?.cartItemAttributions?.length) {
            return undefined;
        }
        return new Set(
            treatmentData.cartItemAttributions
                .filter((a) => a.cartItem?.id != null)
                .map((a) => a.cartItem!.id)
        );
    }, [treatmentData]);

    const initialNewVariants = useMemo((): NewVariantSelection[] | undefined => {
        if (!treatmentData?.cartItemAttributions?.length) {
            return undefined;
        }
        return treatmentData.cartItemAttributions
            .filter((a) => a.cartItem?.id == null && a.treatmentVariant?.id != null)
            .map((a) => ({
                id: crypto.randomUUID(),
                treatmentVariantId: a.treatmentVariant!.id,
                name: a.treatmentVariant!.name,
                treatmentName: a.treatmentVariant!.treatment?.name,
                bundleId: a.bundleId ?? null,
                bundleName: a.bundle?.name,
                priceCents: a.treatmentVariant?.priceCents ?? undefined,
            }));
    }, [treatmentData]);

    const initialCartItemBundleOverrides = useMemo(() => {
        if (!treatmentData?.cartItemAttributions?.length) {
            return undefined;
        }
        const map = new Map<string, { bundleId: string | null; bundleName?: string }>();
        treatmentData.cartItemAttributions
            .filter((a) => a.cartItem?.id != null && a.bundleId != null)
            .forEach((a) => {
                map.set(a.cartItem!.id, { bundleId: a.bundleId!, bundleName: a.bundle?.name });
            });
        return map.size > 0 ? map : undefined;
    }, [treatmentData]);

    const initialProducts = useMemo((): ProductSelection[] | undefined => {
        if (!treatmentData?.cartItemAttributions?.length) {
            return undefined;
        }
        return treatmentData.cartItemAttributions
            .filter((a) => a.physicalProductId != null && a.physicalProduct != null)
            .map((a) => ({
                physicalProductId: a.physicalProduct!.id,
                name: a.physicalProduct!.name,
                unitPriceGross: a.physicalProduct?.unitPriceGross != null ? Number(a.physicalProduct.unitPriceGross) : undefined,
            }));
    }, [treatmentData]);

    const initialMedDocData = useMemo((): IncompleteTreatmentData | undefined => {
        if (!treatmentData) {
            return undefined;
        }
        return {
            id: treatmentData.id,
            appointmentId: treatmentData.appointmentId,
            diagnosis: treatmentData.diagnosis,
            mainIcdId: (treatmentData as any).mainIcdId ?? null,
            mainIcdDisplay: (treatmentData as any).mainIcdDisplay ?? null,
            secondaryIcdId: (treatmentData as any).secondaryIcdId ?? null,
            secondaryIcdDisplay: (treatmentData as any).secondaryIcdDisplay ?? null,
            notes: treatmentData.notes,
            cartItemAttributions: treatmentData.cartItemAttributions,
            medications: treatmentData.medications,
        };
    }, [treatmentData]);

    const isLoadingEditData = isEditMode && open && (isLoadingTreatmentById || (!treatmentById && !treatmentNotFound));

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-5xl max-h-[90vh] flex flex-col">
                {treatmentNotFound ? (
                    <div className="flex flex-col items-center justify-center py-12 gap-4">
                        <p className="text-sm text-muted-foreground">Behandlung nicht gefunden.</p>
                        <Button variant="outline" onClick={() => handleOpenChange(false)}>
                            Schließen
                        </Button>
                    </div>
                ) : isLoadingEditData ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                        <span className="ml-3 text-sm text-muted-foreground">Behandlung wird geladen...</span>
                    </div>
                ) : currentStep === 'ATTRIBUTION_STEP' ? (
                    <AttributionStep
                        customerId={customerId}
                        treatmentId={treatmentId ?? treatmentIdProp}
                        treatmentDateTime={treatmentDateTime}
                        selectedAppointmentId={selectedAppointmentId}
                        setSelectedAppointmentId={setSelectedAppointmentId}
                        initialAppointmentId={treatmentData?.appointmentId ?? undefined}
                        initialCartItemIds={initialCartItemIds}
                        initialNewVariants={initialNewVariants}
                        initialCartItemBundleOverrides={initialCartItemBundleOverrides}
                        initialProducts={initialProducts}
                        initialNoTreatmentReason={treatmentData ? (treatmentData.noTreatmentReason ?? null) : undefined}                        isEditMode={isEditMode}
                        onSaveAndProceed={handleAttributionSaved}
                        onCancel={() => handleOpenChange(false)}
                    />
                ) : currentStep === 'MEDICAL_DOCUMENTATION_STEP' && treatmentId ? (
                    <MedicalDocumentationStep
                        customerId={customerId}
                        treatmentId={treatmentId}
                        treatmentDateTime={treatmentDateTime}
                        initialData={initialMedDocData}
                        isEditMode={isEditMode}
                        isReEdit={isEditMode && (treatmentData as any)?.documentationStep === 'MEDICAL_DOCUMENTATION_STEP'}
                        storeId={appointmentStoreId}
                        onComplete={handleComplete}
                        onBack={handleBack}
                        onCancel={() => handleOpenChange(false)}
                    />
                ) : null}
            </DialogContent>
        </Dialog>
    );
};

export default AddTreatmentDialog;
